import { Component, Injectable, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';

import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent
} from '@tilby/tilby-ui-lib/components/tilby-dialog';

export type StockExportDialogData = {
    email?: string;
    /** Shows the "export on date" field */
    allowDate?: boolean;
};

export type StockExportDialogResult = {
    email: string;
    /** Only set when the user picked a day other than today: end of that day, ISO formatted */
    date?: string;
};

@Component({
    selector: 'app-stock-export-dialog',
    standalone: true,
    imports: [
        MatDatepickerModule,
        MatFormFieldModule,
        MatInputModule,
        ReactiveFormsModule,
        TilbyDialogContentComponent,
        TilbyDialogToolbarComponent,
        TranslateModule
    ],
    templateUrl: './stock-export-dialog.component.html'
})
export class StockExportDialogComponent {
    protected readonly data: StockExportDialogData = inject(MAT_DIALOG_DATA);
    private readonly dialogRef = inject(MatDialogRef<StockExportDialogComponent>);

    protected readonly today = new Date();

    protected readonly form = new FormGroup({
        email: new FormControl(this.data.email ?? '', {
            nonNullable: true,
            validators: [Validators.required, Validators.email, Validators.maxLength(255)]
        }),
        date: new FormControl<Date | null>(this.today, Validators.required)
    });

    protected confirm() {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const { email, date } = this.form.getRawValue();
        const result: StockExportDialogResult = { email };

        if (this.data.allowDate && date && !this.isSameDay(date, this.today)) {
            const endOfDay = new Date(date);
            endOfDay.setHours(23, 59, 59, 999);
            result.date = endOfDay.toISOString();
        }

        this.dialogRef.close(result);
    }

    private isSameDay(a: Date, b: Date) {
        return a.toDateString() === b.toDateString();
    }
}

@Injectable({
    providedIn: 'root'
})
export class StockExportDialogService extends BaseDialogService {
    private readonly dialog = inject(MatDialog);

    /** Resolves with the export parameters, or null if the dialog was cancelled */
    public async openDialog(data: StockExportDialogData = {}): Promise<StockExportDialogResult | null> {
        const config: NonNullableConfigData<StockExportDialogData> = {
            ...this.switchMobileDesktopDimensions({ width: '600px' }),
            data
        };

        const result = await lastValueFrom(this.dialog.open(StockExportDialogComponent, config).afterClosed());

        return result ?? null;
    }
}
